/**
 * Palette des types d'équipement et de POI (à droite de la carte, outil « Placer un équipement »).
 * En haut : « Les plus utilisés » (vos choix récents, puis ce que l'organisation a déjà, puis une
 * base commune : toilettes, eau potable, point de vue…). Dessous : toutes les catégories, avec
 * une recherche. On choisit un type, puis chaque clic sur la carte en place un (Échap pour arrêter).
 * En haut, « Ajouter comme » : équipement de l'organisation (public ou privé) ou POI public de la
 * carte principale. Les tuiles Refuge et Camping créent un lieu (nom demandé, puis fiche complète).
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { POICategory } from '@web/features/map/services/mapApi'

const RECENT_KEY = 'gestion.carte.typesRecents'
/** Base commune si l'organisation n'a encore rien placé. */
const COMMON_SLUGS = [
  'toilets',
  'drinking_water',
  'panorama',
  'parking',
  'picnic_site',
  'trailhead',
  'shower',
  'toilet_vault',
  'reception',
  'waterfall',
  'gate',
  'beach',
]
const FREQUENT_COUNT = 8

type Counts = Record<string, number>

function readRecent(): Counts {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '{}') as Counts
  } catch {
    return {}
  }
}

/** À appeler à chaque équipement placé : le type remonte dans « Les plus utilisés ». */
export function rememberPoiType(slug: string) {
  try {
    const counts = readRecent()
    counts[slug] = (counts[slug] ?? 0) + 1
    localStorage.setItem(RECENT_KEY, JSON.stringify(counts))
  } catch {
    // stockage indisponible : la base commune reste proposée
  }
}

export function poiIcon(t: Pick<POICategory, 'mdi_icon'> | null | undefined, fallback = 'map-marker'): string {
  const name = (t?.mdi_icon || fallback).replace(/^mdi-/, '')
  return `mdi-${name}`
}

export type PlaceAs = 'facility' | 'poi'
export type PlaceKind = 'refuge' | 'camping'

const PLACE_AS_KEY = 'gestion.carte.ajouterComme'

export function defaultPlaceAs(): PlaceAs {
  try {
    return localStorage.getItem(PLACE_AS_KEY) === 'poi' ? 'poi' : 'facility'
  } catch {
    return 'facility'
  }
}

export function rememberPlaceAs(value: PlaceAs) {
  try {
    localStorage.setItem(PLACE_AS_KEY, value)
  } catch {
    // stockage indisponible
  }
}

type Props = {
  categories: POICategory[]
  /** Nombre d'équipements par slug déjà placés par l'organisation. */
  orgCounts: Counts
  selectedId: number | null
  onSelect: (type: POICategory) => void
  placeAs: PlaceAs
  onPlaceAs: (value: PlaceAs) => void
  selectedKind: PlaceKind | null
  onSelectKind: (kind: PlaceKind) => void
  onClose: () => void
}

export function PoiPalette({ categories, orgCounts, selectedId, onSelect, placeAs, onPlaceAs, selectedKind, onSelectKind, onClose }: Props) {
  const { t } = useTranslation('gestion')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const all = useMemo(
    () => categories.flatMap((c) => (c.children ?? []).map((child) => ({ ...child, mdi_icon: child.mdi_icon || c.mdi_icon, icon_color: child.icon_color || c.icon_color }))),
    [categories],
  )
  const bySlug = useMemo(() => new Map(all.map((x) => [x.slug, x])), [all])

  const frequent = useMemo(() => {
    const recent = readRecent()
    const score = (slug: string) => (recent[slug] ?? 0) * 100 + (orgCounts[slug] ?? 0) * 10
    const slugs = [...new Set([...Object.keys(recent), ...Object.keys(orgCounts), ...COMMON_SLUGS])]
      .filter((s) => bySlug.has(s))
      .sort((a, b) => score(b) - score(a) || COMMON_SLUGS.indexOf(a) - COMMON_SLUGS.indexOf(b))
    return slugs.slice(0, FREQUENT_COUNT).map((s) => bySlug.get(s)!)
    // selectedId : la liste se met à jour quand un type vient d'être utilisé.
  }, [bySlug, orgCounts, selectedId])

  const needle = q.trim().toLowerCase()
  const results = needle ? all.filter((x) => x.name.toLowerCase().includes(needle) || x.slug.includes(needle)) : []

  const tile = (x: POICategory) => (
    <button
      key={x.id}
      type="button"
      className={`g-poi${selectedId === x.id ? ' g-poi--active' : ''}`}
      onClick={() => onSelect(x)}
      aria-pressed={selectedId === x.id}
      title={x.name}
    >
      <span className={`mdi ${poiIcon(x)} g-poi__icon`} style={{ color: x.icon_color || undefined }} aria-hidden />
      <span className="g-poi__name">{x.name}</span>
    </button>
  )

  return (
    <aside className="g-palette" aria-label={t('carte.palette')}>
      <div className="g-palette__head">
        <strong>{t('carte.palette')}</strong>
        <button type="button" className="btn btn--icon btn--sm" onClick={onClose} aria-label={t('commun.annuler')}>
          <span className="mdi mdi-close" aria-hidden />
        </button>
      </div>
      <div className="g-field">
        <span className="g-palette__title">{t('carte.ajouter_comme')}</span>
        <div className="g-segmented g-segmented--full" role="radiogroup" aria-label={t('carte.ajouter_comme')}>
          {(['facility', 'poi'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={placeAs === v}
              className={`g-segmented__item${placeAs === v ? ' g-segmented__item--active' : ''}`}
              onClick={() => onPlaceAs(v)}
            >
              <span className={`mdi ${v === 'poi' ? 'mdi-earth' : 'mdi-domain'}`} aria-hidden /> {t(`carte.comme_${v}`)}
            </button>
          ))}
        </div>
        <span className="g-hint">{t(`carte.aide_comme_${placeAs}`)}</span>
      </div>
      <div className="g-palette__title">{t('carte.lieux')}</div>
      <div className="g-poi-grid">
        {(['refuge', 'camping'] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            className={`g-poi${selectedKind === kind ? ' g-poi--active' : ''}`}
            onClick={() => onSelectKind(kind)}
            aria-pressed={selectedKind === kind}
          >
            <span className={`mdi ${kind === 'refuge' ? 'mdi-home-variant-outline' : 'mdi-tent'} g-poi__icon`} aria-hidden />
            <span className="g-poi__name">{t(`types.${kind}`)}</span>
          </button>
        ))}
      </div>
      <input className="g-input" type="search" placeholder={t('carte.rechercher_type')} value={q} onChange={(e) => setQ(e.target.value)} />

      {needle ? (
        <div className="g-poi-grid">{results.length ? results.slice(0, 40).map(tile) : <p className="g-muted">{t('carte.aucun_type')}</p>}</div>
      ) : (
        <>
          <div className="g-palette__title">{t('carte.plus_utilises')}</div>
          <div className="g-poi-grid">{frequent.map(tile)}</div>
          <div className="g-palette__title">{t('carte.categories')}</div>
          <ul className="g-palette__cats">
            {categories.map((c) => (
              <li key={c.id}>
                <button type="button" className="g-palette__cat" onClick={() => setOpen((o) => (o === c.slug ? null : c.slug))} aria-expanded={open === c.slug}>
                  <span className={`mdi ${poiIcon(c)}`} style={{ color: c.icon_color || undefined }} aria-hidden />
                  <span className="g-spacer">{c.name}</span>
                  <span className="g-chip__count">{c.children?.length ?? 0}</span>
                  <span className={`mdi ${open === c.slug ? 'mdi-chevron-up' : 'mdi-chevron-down'}`} aria-hidden />
                </button>
                {open === c.slug && (
                  <div className="g-poi-grid">
                    {(c.children ?? []).map((child) => tile({ ...child, mdi_icon: child.mdi_icon || c.mdi_icon, icon_color: child.icon_color || c.icon_color }))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="g-hint">{t('carte.aide_palette')}</p>
    </aside>
  )
}
